import { inject, Injectable, OnDestroy } from '@angular/core';
import { combineLatest, map, Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserPermission } from 'src/app/shared/models/user-permission';
import { TenantContextService } from 'src/app/shared/tenant-context/tenant-context.service';
import { TenantService } from './tenant.service';

@Injectable({
    providedIn: 'root'
})
export class TenantUserRolePermissionsService implements OnDestroy {
    private currentTenantCode = '';
    private _permissions: UserPermission | null = null;
    private tenantContext = inject(TenantContextService);
    private tenantService = inject(TenantService);
    private latest$ = new ReplaySubject<UserPermission | null>(1);
    private allPermissions$ = new ReplaySubject<UserPermission[] | null>(1);
    private _multiTenantUserPermissions: UserPermission[];
    private destroy$ = new Subject<void>();

    constructor() {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public initTenantRolePermissions(): void {
        this.onTenantChange();
    }

    private onTenantChange() {
        const tenant = this.tenantContext.tenantSelected$;
        const multiTenantUserPermissions = this.tenantService.getUserPermissions();

        combineLatest([multiTenantUserPermissions, tenant])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([p, t]) => {
                this._multiTenantUserPermissions = p;
                this.allPermissions$.next(p);

                if (this.currentTenantCode !== t?.tenantId) {
                    this.currentTenantCode = t?.tenantId || '';
                }
                this.switchTenantUserPermissions();
            });
    }

    private switchTenantUserPermissions() {
        const tenantUserPermissions = this._multiTenantUserPermissions.find(
            userPermission => userPermission.tenantId === this.currentTenantCode
        );
        if (tenantUserPermissions) {
            this._permissions = tenantUserPermissions;
        } else {
            this._permissions = null;
        }
        this.latest$.next(this._permissions);
    }

    public getAllPermissions(): Observable<UserPermission[] | null> {
        return this.allPermissions$;
    }

    public getLatestTenantPermissions(): Observable<UserPermission | null> {
        return this.latest$;
    }

    public hasRequiredUserPermissions(requiredPermissions: string[]): Observable<boolean> {
        return this.getLatestTenantPermissions().pipe(
            map((tenantUserPermissions: UserPermission | null) => {
                return requiredPermissions.every(permission => {
                    return tenantUserPermissions?.permissions.includes(permission);
                });
            })
        );
    }

    public hasAnyRequiredUserPermissions(requiredPermissions: string[]): Observable<boolean> {
        return this.getLatestTenantPermissions().pipe(
            map((tenantUserPermissions: UserPermission | null) => {
                // Check if any requiredPermission exists in userPermissions
                const hasAnyPermission = requiredPermissions.some(permission =>
                    tenantUserPermissions?.permissions.includes(permission)
                );
                return hasAnyPermission;
            })
        );
    }
}
